import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './UserEdit.scss';

const roleLabelMap = {
  Owner: 'Y\u00f6netici',
  Company: 'Firma Kullan\u0131c\u0131s\u0131',
  Accountant: 'Muhasebe',
  RentalPersonnel: 'Kiralama Personeli',
  RentalAccountant: 'Kiralama Muhasebesi',
  TechnicalServiceAccountant: 'Teknik Servis Muhasebesi',
  TechnicalServiceManager: 'Yetkili Servis M\u00fcd\u00fcr\u00fc',
  TechnicalServicePersonnel: 'Yetkili Servis Personeli'
};

const hiddenRoleNames = ['Partner', 'Employee', 'RentalAccountant', 'TechnicalServiceAccountant'];

const roleDisplayOrder = [
  'Owner',
  'Accountant',
  'RentalPersonnel',
  'TechnicalServiceManager',
  'TechnicalServicePersonnel',
  'Company'
];

const UserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    roleId: '',
    email: '',
    phoneNumber: '',
    tc: '',
    password: '',
    id: ''
  });

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const sortedRoles = useMemo(() => {
    return roles
      .filter((role) => !hiddenRoleNames.includes(role.name))
      .sort((a, b) => {
        const orderA = roleDisplayOrder.indexOf(a.name);
        const orderB = roleDisplayOrder.indexOf(b.name);

        if (orderA !== -1 || orderB !== -1) {
          if (orderA === -1) return 1;
          if (orderB === -1) return -1;
          return orderA - orderB;
        }

        const labelA = roleLabelMap[a.name] || a.name;
        const labelB = roleLabelMap[b.name] || b.name;
        return labelA.localeCompare(labelB, 'tr');
      });
  }, [roles]);

  useEffect(() => {
    const fetchUserAndRoles = async () => {
      try {
        const token = localStorage.getItem('token');

        const userResponse = await api.get(`/User/GetByUserId?userId=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const rolesResponse = await api.get('/Auth/GetAllRoles', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const userData = userResponse?.data?.data || {};
        const fetchedRoles = rolesResponse?.data?.data || [];

        setRoles(fetchedRoles);
        setFormData({
          name: userData.name || '',
          surname: userData.surname || '',
          roleId: userData.roleId || fetchedRoles.find((role) => role.name === userData.role)?.id || '',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          tc: userData.tc || userData.TC || '',
          id: userData.id || '',
          password: ''
        });

        setLoading(false);
      } catch (error) {
        console.error('Veriler y\u00fcklenemedi:', error);
        setErrorMessage('Kullan\u0131c\u0131 bilgileri veya roller y\u00fcklenemedi. L\u00fctfen tekrar deneyin.');
        setLoading(false);
      }
    };

    fetchUserAndRoles();
  }, [id]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name === 'tc' && value.length > 11) {
      return;
    }

    if (name === 'phoneNumber' && !/^\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.tc.length !== 11) {
      setErrorMessage('TC Kimlik Numaras\u0131 11 haneli olmal\u0131d\u0131r.');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      await api.put('/User/', formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSuccessMessage('Kullan\u0131c\u0131 ba\u015far\u0131yla g\u00fcncellendi.');
      setErrorMessage('');
      navigate('/user-list');
    } catch (error) {
      console.error('Kullan\u0131c\u0131 g\u00fcncellenemedi:', error);
      setErrorMessage('Kullan\u0131c\u0131 g\u00fcncellenemedi. L\u00fctfen bilgileri kontrol edin.');
      setSuccessMessage('');
    }
  };

  if (loading) {
    return <p>{'Y\u00fckleniyor...'}</p>;
  }

  return (
    <div className="user-edit-page">
      <Navbar />
      <div className="form-wrapper">
        <h2>{'Kullan\u0131c\u0131 D\u00fczenle'}</h2>
        {errorMessage && <p className="error-message">{errorMessage}</p>}
        {successMessage && <p className="success-message">{successMessage}</p>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Ad</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ad"
                required
              />
            </div>
            <div className="form-group">
              <label>Soyad</label>
              <input
                type="text"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                placeholder="Soyad"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Telefon</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="Telefon"
                required
              />
            </div>
            <div className="form-group">
              <label>E-posta</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="E-posta"
                disabled
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>TC Kimlik No</label>
              <input
                type="text"
                name="tc"
                value={formData.tc}
                onChange={handleInputChange}
                placeholder="TC Kimlik No"
                required
              />
            </div>
            <div className="form-group">
              <label>{'Rol Se\u00e7in'}</label>
              <select
                name="roleId"
                value={formData.roleId || ''}
                onChange={handleInputChange}
                required
              >
                <option value="">{'Rol Se\u00e7in'}</option>
                {sortedRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {roleLabelMap[role.name] || role.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button type="submit">{'G\u00fcncelle'}</button>
        </form>
      </div>
    </div>
  );
};

export default UserEdit;

